// =============================================================================
// ui/tabs/PatternEditor.tsx — the Edit Pattern modal.
// -----------------------------------------------------------------------------
// Transcribed from Josh's screenshot, top to bottom: the six-cell stats strip,
// RULE VERSIONS, IDENTITY (name / description / entry criteria / legacy rules /
// invalidation), the Exit-Model callout, CATEGORY, CONTEXT FILTERS, and
// Delete · Cancel · Save.
//
// ── 🚨 THE SAVE IS NOT A FIELD COPY. READ D76 BEFORE CHANGING ANYTHING HERE ──
//
// Editing a PRODUCTION pattern's rules must never touch the live version — trades
// are pinned to whichever version was live when they were tagged, so rewriting it
// re-judges a year of trades against rules written afterwards. `savePattern` runs
// `reconcileVersions` for exactly that, and this form's only job is to hand it the
// working rules honestly.
//
// ⚠ WHICH IS WHY PROMOTE AND REJECT DO NOT GO THROUGH THE SAVE BUTTON. They call
// `savePatternLifecycle` and close, matching the old client. Routing them through
// the form would reconcile over rules the user never opened and, on a pattern with
// a candidate in flight, immediately re-create the thing just promoted.
//
// ── WHY THIS DOES NOT HYDRATE, WHERE THE TRADE FORM MUST (D71) ──
//
// The trade form re-reads its record because it edits 31 of 48 columns and a
// partial read would blank the rest. `loadPatterns` already selects every column
// `PatternPatch` writes, and this form renders every one of them — so the record
// on screen IS the record being written. The property comes from the shape of the
// read, not from a guard.
//
// ⚠ Recheck that the day `patterns` gains a column. A field this form does not
// render, added to `PatternPatch`, is the write D46 forbids and nothing here would
// notice.
//
// ── DEFERRED, AND NAMED ON SCREEN ──
//
// The per-pattern EQUITY CURVE, EXAMPLE SCREENSHOTS, and the category MANAGER
// behind "✎ Edit categories".
//
// ⚠ THE REASONS THAT USED TO BE HERE WERE BOTH FALSE, and they had reached the
// screen: "no chart library is chosen in this client yet" (`charts/EquityCurve`
// ships and renders on three tabs) and "needs Storage io" (`io/storage/media`
// exists; `media/ScreenshotGrid` uploads through it and `media/Lightbox`
// displays from it). Both features really are missing from this form — what was
// wrong was the REASON, which told a reader the client could not do it when the
// truth is that nobody has wired it here.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { usePatternCap } from '../state/useFreeTierCap';
import { FreeTierLimit } from '../shell/FreeTierLimit';
import { useTabMemoryDraft } from '../state/tabMemory';
import {
  deletePattern, insertPattern, savePattern, savePatternLifecycle, validatePattern,
  type PatternPatch,
} from '../../io/sync/savePattern';
import {
  patternCriteria, patternLifecycle, type PatternSummary,
} from '../../io/sync/patterns';
import {
  candidateVersionOf, criterionId, promoteVersion, rejectVersion,
  type Criterion, type PatternVersion, type VersionedPattern,
} from '../../selectors/patternVersions';
import type { PatternCategory, PatternStats } from '../../selectors/patternBoard';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { num, pct, unitCompact } from '../format';

/** The old client's own lists, in its own order. */
const SESSIONS = ['Asia', 'EU', 'NY-AM', 'NY-PM'] as const;
const REGIMES = ['Trending', 'Range', 'Choppy'] as const;
/** ⚠ INDEX IS THE STORED VALUE. 0 = Sun … 6 = Sat, and the column is INTEGER[]. */
const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const NEWS_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'no-news', label: 'No-news only' },
  { value: 'news-only', label: 'News-driven only' },
] as const;

export interface PatternEditorProps {
  /** `null` opens a blank form — the New Pattern path. */
  pattern: PatternSummary | null;
  /** Null for a new pattern, which has no trades yet by definition. */
  stats: PatternStats | null;
  categories: readonly PatternCategory[];
  /**
   * The display unit, PASSED IN rather than read from a hook.
   *
   * 🚨 So the strip and the card behind it are formatting the same value with
   * the same unit (#67) — and so this modal keeps working in a test that renders
   * it on its own, which a hook into the preferences provider would have broken.
   */
  unit: PnlUnit;
  newId: () => string;
  now: () => string;
  onSaved: () => void;
  onDeleted: () => void;
  onCancel: () => void;
  /** Fired after a promote or reject writes — the tab reloads the catalogue. */
  onLifecycle: () => void;
}

export function PatternEditor(props: PatternEditorProps) {
  const {
    pattern, stats, categories, unit, newId, now,
    onSaved, onDeleted, onCancel, onLifecycle,
  } = props;
  const { sb, userId } = useSupabase();
  const isNew = pattern == null;

  // ── 🔴 THE DRAFT OUTLIVES THE TAB, BUT CANCEL STILL DISCARDS IT (TCR-80) ──
  //
  // Leaving the Patterns tab destroys this modal (D94), and with it a
  // half-written setup. ⚠ Autosave is deliberately NOT the fix: this is a modal
  // with a Cancel that discards, so writing on blur would make that button a
  // lie. The draft lives above the tab; only Save reaches the database.
  // Decided by Josh, 2026-10-06. ⚠ Keyed per pattern. ⚠ A reload loses it —
  // `tabMemory` is session-scoped on purpose, and its header says why.
  const [values, setValues, discardValues] = useTabMemoryDraft<PatternPatch>(
    `patterns:draft:${pattern?.id ?? 'new'}`,
    {
    name: pattern?.name === '(untitled)' ? '' : (pattern?.name ?? ''),
    description: pattern?.description ?? '',
    criteria: pattern?.criteria ?? '',
    entryTrigger: pattern?.entryTrigger ?? '',
    invalidation: pattern?.invalidation ?? '',
    entryCriteria: patternCriteria(pattern),
    category: pattern?.category ?? '',
    sessions: [...(pattern?.sessions ?? [])],
    regimes: [...(pattern?.regimes ?? [])],
    daysOfWeek: [...(pattern?.daysOfWeek ?? [])],
    priorDayRel: pattern?.priorDayRel ?? '',
    news: pattern?.news ?? '',
    },
  );
  /**
   * ⚠ THE PENDING ADD-INPUT IS FLUSHED ON SAVE, and that is a bug the old client
   * already fixed rather than a nicety. A criterion typed and then Save-clicked —
   * the common case — is otherwise silently dropped, which reads to the user as
   * "it won't let me add more than N criteria".
   */
  // ⚠ REMEMBERED TOO, for the reason the comment above gives: this is text the
  // trader typed. The flush-on-save behaviour documented just above only helps
  // if the box still holds it when they come back to the pattern.
  const [pending, setPending, discardPending] = useTabMemoryDraft<string>(
    `patterns:draftpending:${pattern?.id ?? 'new'}`,
    '',
  );
  const [busy, setBusy] = useState(false);
  // 🚨 THE FREE-TIER CAP — #65. Consumed by the refusal branch below.
  const { blocked: capped } = usePatternCap();
  const [error, setError] = useState<string | null>(null);

  /** Both slots go together — a pattern draft is the pair, not either half. */
  const discardDraft = useCallback(
    () => { discardValues(); discardPending(); },
    [discardValues, discardPending],
  );

  /** Throw the draft away, then leave — Cancel, the ×, and the cap's close. */
  const cancel = useCallback(() => { discardDraft(); onCancel(); }, [discardDraft, onCancel]);

  const set = <K extends keyof PatternPatch>(k: K, v: PatternPatch[K]) =>
    setValues((cur) => ({ ...cur, [k]: v }));

  const toggle = (k: 'sessions' | 'regimes', v: string) =>
    setValues((cur) => ({
      ...cur,
      [k]: cur[k].includes(v) ? cur[k].filter((x) => x !== v) : [...cur[k], v],
    }));

  const toggleDow = (d: number) =>
    setValues((cur) => ({
      ...cur,
      daysOfWeek: cur.daysOfWeek.includes(d)
        ? cur.daysOfWeek.filter((x) => x !== d)
        : [...cur.daysOfWeek, d],
    }));

  // ── entry criteria ─────────────────────────────────────────────────────────

  const addCriterion = (label: string) => {
    const text = label.trim();
    if (!text) return;
    setValues((cur) => ({
      ...cur,
      // ⚠ Slugged from the label and uniquified against the ids ALREADY HERE. An
      // existing criterion keeps its stored id even when its text is edited,
      // because that id keys a trade's `criteriaMet` map — re-slugging on edit
      // would orphan every tick recorded against it.
      entryCriteria: [
        ...cur.entryCriteria,
        { id: criterionId(text, cur.entryCriteria.map((c) => c.id)), label: text },
      ],
    }));
  };

  const editCriterion = (id: string, label: string) =>
    setValues((cur) => ({
      ...cur,
      entryCriteria: cur.entryCriteria.map((c) => (c.id === id ? { ...c, label } : c)),
    }));

  const removeCriterion = (id: string) =>
    setValues((cur) => ({
      ...cur,
      entryCriteria: cur.entryCriteria.filter((c) => c.id !== id),
    }));

  /** The committed list, with any un-Entered text flushed in and blanks dropped. */
  const readCriteria = useCallback((): Criterion[] => {
    const committed = values.entryCriteria.filter((c) => c.label.trim());
    const text = pending.trim();
    if (!text) return committed;
    return [...committed, { id: criterionId(text, committed.map((c) => c.id)), label: text }];
  }, [values.entryCriteria, pending]);

  // ── the version panel ──────────────────────────────────────────────────────

  /**
   * ⚠ Built from the STORED pattern, never from `values`. The panel describes what
   * is on disk; showing it against unsaved edits would let a user promote a
   * candidate whose criteria are not the ones they can see.
   */
  const stored = useMemo<VersionedPattern | null>(
    () => (pattern ? patternLifecycle(pattern) : null), [pattern],
  );

  const [rejectReason, setRejectReason] = useState('');

  const runPromoteVersion = useCallback(async () => {
    if (!pattern || !stored) return;
    if (!window.confirm(
      'Promote this candidate version to live? It becomes the rules that score new '
      + 'live trades — your already-tagged trades keep the version they were pinned to.',
    )) return;
    setBusy(true);
    setError(null);
    // ⚠ `null` rules: a promote changes no working rules, so the invalidation
    // column and `extras.entryCriteria` must not be named in the UPDATE.
    const res = await savePatternLifecycle(
      sb, pattern.id, pattern, promoteVersion(stored), userId, null,
    );
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onLifecycle();
  }, [sb, userId, pattern, stored, onLifecycle]);

  const runRejectVersion = useCallback(async () => {
    if (!pattern || !stored) return;
    if (!window.confirm(
      'Reject this candidate version? It stays in the version history as a '
      + 'tried-and-failed record, and the working rules revert to the live version.',
    )) return;
    setBusy(true);
    setError(null);
    // ⚠ The reverted RULES ride along. Dropping the candidate without them leaves
    // rules on the record that exist in no version, and the next save re-creates
    // the candidate just rejected (D76).
    const { lifecycle, rules } = rejectVersion(stored, rejectReason);
    const res = await savePatternLifecycle(sb, pattern.id, pattern, lifecycle, userId, rules);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onLifecycle();
  }, [sb, userId, pattern, stored, rejectReason, onLifecycle]);

  // ── save / delete ──────────────────────────────────────────────────────────

  const save = useCallback(async () => {
    const patch: PatternPatch = { ...values, entryCriteria: readCriteria() };
    const invalid = validatePattern(patch);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    setError(null);
    const res = isNew
      ? await insertPattern(sb, newId(), patch, userId, now())
      : await savePattern(sb, pattern.id, patch, pattern, userId, now());
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    discardDraft();
    onSaved();
  }, [sb, userId, values, readCriteria, isNew, pattern, newId, now, onSaved, discardDraft]);

  const remove = useCallback(async () => {
    if (!pattern) return;
    // ⚠ TWO consequences, both named. `trades.pattern_id` is ON DELETE SET NULL,
    // so this UNTAGS every trade rather than failing; and a pattern carries its
    // version history, so it also discards every rule set ever tested. Neither is
    // undone by re-creating a pattern with the same name.
    const tail = stats?.sample
      ? `\n\n${stats.sample} tagged trade${stats.sample === 1 ? '' : 's'} will be left with `
        + 'no setup, and the whole rule-version history goes with it. Neither comes back '
        + 'if you re-create it.'
      : '\n\nNo trades are tagged with it. Its rule-version history goes too.';
    if (!window.confirm(`Delete "${pattern.name}"?${tail}`)) return;
    setBusy(true);
    setError(null);
    const res = await deletePattern(sb, userId, pattern.id);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    discardDraft();
    onDeleted();
  }, [sb, userId, pattern, stats, onDeleted, discardDraft]);

  const hasLegacy = Boolean(values.criteria.trim() || values.entryTrigger.trim());

  // ── 🚨 THE CAP REFUSES BEFORE THE FORM, NOT AFTER IT — #65 ──
  //
  // This editor is the ONLY caller of `insertPattern`, which holds the only
  // insert into `patterns` in the whole client — so refusing here covers every
  // path there is. There is no bulk create, no import and no duplicate gesture.
  //
  // ⚠ `isNew` is the WHOLE condition. Editing an existing pattern is never
  // capped and neither is deleting one; the old client gates creation only, and
  // a cap that blocked edits would strand a free user with rows they cannot fix.
  //
  // ⚠ Retired and archived patterns still COUNT toward the cap — see
  // domain/freeTier.ts. ⚠ INERT while PAID_LAUNCH is false.
  if (isNew && capped) {
    return <FreeTierLimit title="New pattern" message={capped} onClose={cancel} />;
  }

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Edit Pattern">
      <div className="modal wide pat-modal">
        <header className="modal-head">
          <b>{isNew ? 'New Pattern' : 'Edit Pattern'}</b>
          <button type="button" className="btn" onClick={cancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body">
          {/* ⚠ Only for a pattern with a history. A new one shows nothing rather
              than six zeros, which reads as a setup that has been traded and lost
              money. */}
          {stats && !isNew ? (
            <div className="card pat-stats">
              <Stat label="Sample" value={String(stats.sample)} />
              <Stat label="Win Rate" value={stats.sample ? pct(stats.winRate, 0) : '—'} />
              <Stat label="Avg R" value={stats.sample ? num(stats.avgR, 2) : '—'} />
              {/* 🚨 THESE ARE ALREADY IN THE DISPLAY UNIT — only the `$` was wrong
                  (#67). `patternBoard` is handed `tradeValue(t, unit, contracts)`,
                  so on Points these are points, and `money()` was stamping a
                  dollar sign on them. Josh's beta screenshot settles the
                  direction: the strip reads "+2 pts · +52 pts" there, so the
                  VALUE was right and the LABEL was the lie.
                  ⚠ The Journal's Live P&L needs the OPPOSITE fix — beta leaves
                  that one in dollars. Do not "fix" both by matching on `money(`. */}
              <Stat
                label="Expectancy" value={stats.sample ? unitCompact(stats.expectancy, unit) : '—'}
                cls={stats.expectancy > 0 ? 'green' : stats.expectancy < 0 ? 'red' : ''}
              />
              <Stat
                label="Total P&L" value={stats.sample ? unitCompact(stats.totalPnl, unit) : '—'}
                cls={stats.totalPnl > 0 ? 'green' : stats.totalPnl < 0 ? 'red' : ''}
              />
              <Stat
                label="Last 30d"
                value={`${stats.last30Trades} / ${unitCompact(stats.last30Pnl, unit)}`}
              />
            </div>
          ) : null}

          {/* ⚠ NAMED, not silently absent — the old client draws a per-pattern
              equity curve under the strip whenever the sample is 3 or more. */}
          {stats && !isNew && stats.sample >= 3 ? (
            <p className="muted pat-nocurve">
              Beta also draws an <b>equity curve for this pattern only</b> here. This client
              charts elsewhere already — it just has not been wired onto this form yet.
            </p>
          ) : null}

          {stored ? (
            <VersionPanel
              stored={stored}
              reason={rejectReason}
              onReason={setRejectReason}
              busy={busy}
              onPromote={() => void runPromoteVersion()}
              onReject={() => void runRejectVersion()}
            />
          ) : null}

          <h4 className="tw2-head">Identity</h4>
          <div className="form-grid">
            <label className="field full">
              <span className="field-label">Name</span>
              <input
                value={values.name} autoFocus
                placeholder="NY-AM Failed Auction Reversal"
                onChange={(e) => set('name', e.target.value)}
              />
            </label>
            <label className="field full">
              <span className="field-label">Short Description</span>
              <input
                value={values.description} placeholder="One-line summary"
                onChange={(e) => set('description', e.target.value)}
              />
            </label>

            <div className="field full">
              <span className="field-label" id="pf-crit-label">
                Entry Criteria
                <span className="muted lbl-note">
                  — become checkboxes on the trade form when this pattern is tagged
                  (leave empty for none)
                </span>
              </span>
              <div className="pat-crit-list">
                {values.entryCriteria.map((c) => (
                  <div className="pat-crit-row" key={c.id}>
                    <input
                      value={c.label}
                      aria-label={`Criterion: ${c.label}`}
                      placeholder="e.g. Absorption at the level"
                      onChange={(e) => editCriterion(c.id, e.target.value)}
                    />
                    <button
                      type="button" className="btn danger"
                      aria-label={`Remove criterion ${c.label}`}
                      onClick={() => removeCriterion(c.id)}
                    >×</button>
                  </div>
                ))}
              </div>
              <input
                className="pat-crit-add"
                aria-labelledby="pf-crit-label"
                placeholder="+ add criterion, Enter"
                value={pending}
                onChange={(e) => setPending(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  addCriterion(pending);
                  setPending('');
                }}
              />
            </div>

            {/* ⚠ BOTH legacy fields, and both cleared together. `criteria` and
                `entry_trigger` are two columns holding pre-itemization prose; a
                Clear button that wiped only one would tell the user the note is
                gone while a paragraph stays on the row with nothing left to show
                it. */}
            {hasLegacy ? (
              <div className="field full">
                <details className="pat-legacy">
                  <summary>
                    Legacy rules text
                    <span className="lbl-note"> — from before criteria were itemized</span>
                  </summary>
                  <div className="pat-legacy-body">
                    {values.criteria.trim() ? (
                      <div><b>Criteria / Rules:</b> {values.criteria}</div>
                    ) : null}
                    {values.entryTrigger.trim() ? (
                      <div><b>Entry trigger:</b> {values.entryTrigger}</div>
                    ) : null}
                    <p className="muted">
                      Re-enter these as Entry Criteria items above, then clear this note.
                    </p>
                    <button
                      type="button" className="btn danger"
                      onClick={() => setValues((cur) => ({ ...cur, criteria: '', entryTrigger: '' }))}
                    >
                      Clear legacy text
                    </button>
                    <span className="muted"> Save to persist.</span>
                  </div>
                </details>
              </div>
            ) : null}

            <label className="field full">
              <span className="field-label">
                Invalidation
                <span className="muted lbl-note">— where the setup is wrong / you’re out</span>
              </span>
              <textarea
                rows={2} value={values.invalidation}
                onChange={(e) => set('invalidation', e.target.value)}
              />
            </label>

            <div className="field full">
              <p className="pat-callout">
                <b>Stop, target, and scale-out rules live on Exit Models</b> — not on the
                pattern. The same setup can have multiple exit approaches (e.g. quick-partial
                vs. runner). Manage them on the <b>Exit Models</b> sub-tab; tag each trade
                with the exit model you actually used.
              </p>
            </div>

            <div className="field full">
              <span className="field-label">
                Category
                <span className="muted lbl-note">(single — click again to clear)</span>
              </span>
              <div className="toggle-group" role="group" aria-label="Category">
                {categories.map((c) => {
                  const active = values.category === c.name;
                  return (
                    <button
                      key={c.name} type="button" className={active ? 'active' : ''}
                      aria-pressed={active}
                      // ⚠ SINGLE-SELECT, and clicking the active one CLEARS it —
                      // that is how a pattern becomes uncategorized, and there is
                      // no other control for it.
                      onClick={() => set('category', active ? '' : c.name)}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="muted field-hint">
                Editing the category list itself isn’t built here yet — beta has
                “✎ Edit categories”. Your {categories.length} categories are read from
                settings and shown above.
              </p>
            </div>
          </div>

          <h4 className="tw2-head">Context Filters</h4>
          <div className="field">
            <span className="field-label">Valid Sessions</span>
            <div className="toggle-group" role="group" aria-label="Valid Sessions">
              {SESSIONS.map((s) => (
                <button
                  key={s} type="button" aria-pressed={values.sessions.includes(s)}
                  className={values.sessions.includes(s) ? 'active' : ''}
                  onClick={() => toggle('sessions', s)}
                >{s}</button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="field-label">Market Regimes</span>
            <div className="toggle-group" role="group" aria-label="Market Regimes">
              {REGIMES.map((r) => (
                <button
                  key={r} type="button" aria-pressed={values.regimes.includes(r)}
                  className={values.regimes.includes(r) ? 'active' : ''}
                  onClick={() => toggle('regimes', r)}
                >{r}</button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="field-label">Days of Week</span>
            <div className="toggle-group" role="group" aria-label="Days of Week">
              {DOW_NAMES.map((d, i) => (
                <button
                  key={d} type="button" aria-pressed={values.daysOfWeek.includes(i)}
                  className={values.daysOfWeek.includes(i) ? 'active' : ''}
                  // ⚠ THE INDEX, not the name. The column is INTEGER[] and Josh's
                  // VWAP PDC stores [1,2,3,4,5]. Storing 'Mon' would type-check
                  // against the old `string[]` model and be wrong on the row.
                  onClick={() => toggleDow(i)}
                >{d}</button>
              ))}
            </div>
          </div>
          <div className="form-grid cols-2">
            <label className="field">
              <span className="field-label">Prior-day Relationship</span>
              <input
                value={values.priorDayRel} placeholder="e.g. close above VAH"
                onChange={(e) => set('priorDayRel', e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">News</span>
              <select value={values.news} onChange={(e) => set('news', e.target.value)}>
                {NEWS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <h4 className="tw2-head">Example Screenshots</h4>
          <p className="muted">
            Not built here yet — this form cannot add or show them, though the client does
            read Storage elsewhere. Anything you pasted in beta is still on the record and
            is not touched by saving here.
          </p>

          {error ? (
            <div role="alert" className="import-alert">
              <b>Nothing was saved.</b> {error}
            </div>
          ) : null}
        </div>

        <footer className="modal-foot import-row">
          {!isNew ? (
            <button type="button" className="btn danger" disabled={busy} onClick={() => void remove()}>
              Delete
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn" disabled={busy} onClick={cancel}>Cancel</button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// =============================================================================

const BADGES: Record<string, { label: string; cls: string }> = {
  live: { label: 'Live', cls: 'live' },
  candidate: { label: 'Candidate', cls: 'cand' },
  super: { label: 'Superseded', cls: 'super' },
  rej: { label: 'Rejected', cls: 'rej' },
};

/**
 * RULE VERSIONS.
 *
 * ⚠ GATED exactly as the old client gates it — hidden unless there is a candidate
 * in flight OR more than one version. That is why it is absent from Josh's Edit
 * Pattern screenshot: his VWAP PDC has neither. A panel that always rendered would
 * put a "v1 · Live" row on all eleven patterns and make a one-version pattern look
 * like it has a history.
 */
function VersionPanel(
  { stored, reason, onReason, busy, onPromote, onReject }: {
    stored: VersionedPattern;
    reason: string;
    onReason: (v: string) => void;
    busy: boolean;
    onPromote: () => void;
    onReject: () => void;
  },
) {
  const versions = stored.versions;
  if (!candidateVersionOf(stored) && versions.length <= 1) return null;

  // ⚠ Newest first, by the integer `v` — NOT by array order and NOT by
  // `createdAt`. Rejected versions stay in the list, so the array order and the
  // numbering diverge the moment one is rejected.
  const ordered = [...versions].sort((a, b) => b.v - a.v);

  return (
    <div className="card pat-versions">
      <h4 className="tw2-head">Rule Versions</h4>
      <p className="muted">
        Live trades stay pinned to the version they were tagged under. Editing the criteria
        below spins a candidate — promote it to make it the live rules, or reject it to file
        it away.
      </p>
      {ordered.map((ver) => (
        <VersionRow
          key={ver.v} ver={ver}
          inFlight={stored.candidateVersion === ver.v && stored.status === 'production'}
          reason={reason} onReason={onReason} busy={busy}
          onPromote={onPromote} onReject={onReject}
        />
      ))}
    </div>
  );
}

function VersionRow(
  { ver, inFlight, reason, onReason, busy, onPromote, onReject }: {
    ver: PatternVersion;
    inFlight: boolean;
    reason: string;
    onReason: (v: string) => void;
    busy: boolean;
    onPromote: () => void;
    onReject: () => void;
  },
) {
  const badge = BADGES[ver.status] ?? { label: ver.status, cls: 'super' };
  const crits = ver.entryCriteria.map((c) => c.label).filter(Boolean).join(' · ');
  return (
    <div className="pat-ver">
      <div className="pat-ver-head">
        <b>v{ver.v}</b>
        <span className={`pat-ver-badge ${badge.cls}`}>{badge.label}</span>
      </div>
      <div className="muted pat-ver-rules">
        {/* ⚠ "(no criteria)" rather than an empty line — a version with no entry
            criteria is a real and meaningful state, and a blank row reads as a
            rendering failure. */}
        {crits || '(no criteria)'}
        {ver.invalidation ? (
          <> · <span className="dim">invalidation:</span> {ver.invalidation}</>
        ) : null}
      </div>
      {ver.status === 'rej' && ver.reason ? (
        <div className="pat-ver-reason">✕ {ver.reason}</div>
      ) : null}
      {inFlight ? (
        <div className="pat-ver-actions">
          <input
            aria-label="Reason to reject" placeholder="reason to reject (optional)"
            value={reason} onChange={(e) => onReason(e.target.value)}
          />
          <button type="button" className="btn primary" disabled={busy} onClick={onPromote}>
            ▲ Promote to live
          </button>
          <button type="button" className="btn danger" disabled={busy} onClick={onReject}>
            ✕ Reject
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="stat-cell">
      <div className="slabel">{label}</div>
      <div className={`sval ${cls ?? ''}`}>{value}</div>
    </div>
  );
}
